import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from 'src/hooks/useAnalytics';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import useOrganization from 'src/hooks/useOrganization';
import { Caller } from './components/Caller';
import { Feedback } from './components/Feedback';
import { Ready } from './components/Ready';
import { Welcome } from './components/Welcome';
import { OnboardingPhase } from './components/onboardingPhase.type';

export const Onboarding = () => {
  const { get } = useAuthedRequest();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const [currentPhase, setCurrentPhase] = useState<OnboardingPhase>(
    OnboardingPhase.Welcome,
  );
  const { track } = useAnalytics();

  useEffect(() => {
    const fetchOnboarding = async () => {
      if (!organization.id) return;

      try {
        const response = await get(
          `/organizations/${organization.id}/onboarding`,
        );

        if (
          response &&
          Object.keys(response).length > 0 &&
          response?.currentPhase
        ) {
          setCurrentPhase(response?.currentPhase || OnboardingPhase.Welcome);
        } else {
          throw new Error('No onboarding data found');
        }
      } catch (error) {
        console.error('Error fetching onboarding data', error);
        track.errorExperienced({
          error: 'fetch_failed',
          message: 'Error fetching onboarding data',
        });
        navigate('/');
      }
    };

    fetchOnboarding();
  }, [organization.id]);

  const renderPhase = () => {
    switch (currentPhase) {
      case OnboardingPhase.Welcome:
        return (
          <Welcome
            onPhaseComplete={() => {
              setCurrentPhase(OnboardingPhase.Caller);
              track.formSubmitted({
                formName: 'Onboarding: Welcome',
                success: true,
              });
            }}
            organization={organization}
          />
        );
      case OnboardingPhase.Caller:
        return (
          <Caller
            onPhaseComplete={() => {
              setCurrentPhase(OnboardingPhase.Feedback);
              track.formSubmitted({
                formName: 'Onboarding: Caller',
                success: true,
              });
            }}
            organization={organization}
            secondaryAction={() => {
              navigate('/agents');
            }}
          />
        );
      case OnboardingPhase.Feedback:
        return (
          <Feedback
            onPhaseComplete={() => {
              track.formSubmitted({
                formName: 'Onboarding: Feedback',
                success: true,
              });
              navigate('/agents');
            }}
            organization={organization}
            secondaryAction={() => {
              setCurrentPhase(OnboardingPhase.Welcome);
            }}
          />
        );
      case OnboardingPhase.Ready:
        return (
          <Ready
            onPhaseComplete={() => {
              track.formSubmitted({
                formName: 'Onboarding: Ready',
                success: true,
              });
              navigate('/agents');
            }}
            organization={organization}
            secondaryAction={() => {
              setCurrentPhase(OnboardingPhase.Welcome);
            }}
          />
        );
      default:
        return (
          <Welcome
            onPhaseComplete={() => {
              setCurrentPhase(OnboardingPhase.Caller);
              track.formSubmitted({
                formName: 'Onboarding: Welcome',
                success: true,
              });
            }}
            organization={organization}
          />
        );
    }
  };

  return <div className="min-h-screen">{renderPhase()}</div>;
};
